import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axiosInstance from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { stripeCardElementOptions } from "../../components/custom/StripePaymentSection.jsx";
import { useClearScopedAccountOnMount } from "../../utils/useClearScopedAccountOnMount";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    "pk_live_51TcWrnEFp6E1oRogJ6ul5KZwwOONiiQlsq1WfQiz6KFzwtF6k4ktPHC1ykVZiWZd67Wl2S9UA71VlHxMEJBddZmL00RkevipOk"
);

const MAX_EXTRA_SLOTS = 50;

function ExtraPondForm() {
  useClearScopedAccountOnMount();
  const { id } = useParams();
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [fisher, setFisher] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [checkout, setCheckout] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const checkoutRequestId = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const fisherRes = await axiosInstance.get(`api/company/accounts/${id}`);
        setFisher(fisherRes.data);
      } catch (error) {
        showErrorToast(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Could not load account"
        );
        history.push(`/company/accounts/${id}`);
      } finally {
        setLoadingAccount(false);
      }
    })();
  }, [id, history]);

  const loadCheckout = useCallback(
    async (qty) => {
      const requestId = ++checkoutRequestId.current;
      setLoadingCheckout(true);
      setCheckout(null);
      try {
        const checkoutRes = await axiosInstance.post(
          `api/company/accounts/${id}/extra-pond-checkout`,
          { quantity: qty }
        );
        if (requestId !== checkoutRequestId.current) return;
        setCheckout(checkoutRes.data);
      } catch (error) {
        if (requestId !== checkoutRequestId.current) return;
        showErrorToast(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "Could not start extra pond checkout"
        );
        history.push(`/company/accounts/${id}`);
      } finally {
        if (requestId === checkoutRequestId.current) {
          setLoadingCheckout(false);
        }
      }
    },
    [id, history]
  );

  useEffect(() => {
    if (loadingAccount || !fisher) return;
    loadCheckout(quantity);
  }, [loadingAccount, fisher, quantity, loadCheckout]);

  const clampQuantity = (value) => {
    const n = parseInt(value, 10);
    if (!Number.isFinite(n)) return 1;
    return Math.max(1, Math.min(MAX_EXTRA_SLOTS, n));
  };

  const handleQuantityChange = (next) => {
    const raw = parseInt(next, 10);
    const clamped = clampQuantity(next);
    if (Number.isFinite(raw) && raw !== clamped) {
      if (raw < 1) showErrorToast("Quantity must be at least 1");
      else if (raw > MAX_EXTRA_SLOTS) showErrorToast(`Quantity cannot exceed ${MAX_EXTRA_SLOTS}`);
    }
    setQuantity(clamped);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !checkout?.clientSecret) return;
    setSubmitting(true);
    try {
      const card = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        checkout.clientSecret,
        { payment_method: { card } }
      );
      if (stripeError) {
        showErrorToast(stripeError.message || "Payment failed");
        setSubmitting(false);
        return;
      }
      if (paymentIntent?.status !== "succeeded") {
        showErrorToast("Payment did not complete");
        setSubmitting(false);
        return;
      }

      const purchaseRes = await axiosInstance.post(`api/company/accounts/${id}/extra-pond`, {
        StripePaymentIntentId: paymentIntent.id,
      });
      showSuccessToast(
        purchaseRes.data?.notification ||
          purchaseRes.data?.message ||
          "Extra pond slot(s) added"
      );
      history.push(`/company/accounts/${id}`);
    } catch (error) {
      showErrorToast(
        error.response?.data?.message || error.response?.data?.error || "Purchase failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingAccount || !fisher) return <Preloader />;

  const unitAmount = checkout?.unitAmount ?? null;
  const totalAmount = checkout?.amount ?? (unitAmount != null ? unitAmount * quantity : null);
  const currentLicensed =
    checkout?.licensedPondCount ?? fisher.LicensedPondCount ?? 1;
  const afterPurchase = currentLicensed + quantity;

  return (
    <div className="container-fluid py-3" style={{ maxWidth: 720 }}>
      <Link to={`/company/accounts/${id}`} className="text-muted">
        ← Back
      </Link>
      <h1 className="page-header mt-2">Buy extra pond slots</h1>
      <p className="text-muted">
        {fisher.FullName} — {fisher.Email}
      </p>
      <form onSubmit={handleSubmit} className="card p-4">
        <p className="mb-3">
          Current licensed ponds: <strong>{currentLicensed}</strong>
          {checkout?.packageName || fisher.Package?.Name ? (
            <>
              {" "}
              · Package:{" "}
              <strong>{checkout?.packageName || fisher.Package?.Name}</strong>
            </>
          ) : null}
        </p>

        <div className="mb-3">
          <label className="form-label fw-semibold" htmlFor="extra-pond-qty">
            How many pond slots to add?
          </label>
          <div className="d-flex align-items-center gap-2" style={{ maxWidth: 280 }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              aria-label="Decrease quantity"
              disabled={quantity <= 1 || loadingCheckout || submitting}
              onClick={() => handleQuantityChange(quantity - 1)}
            >
              −
            </button>
            <input
              id="extra-pond-qty"
              type="number"
              className="form-control text-center"
              min={1}
              max={MAX_EXTRA_SLOTS}
              value={quantity}
              disabled={loadingCheckout || submitting}
              onChange={(e) => handleQuantityChange(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              aria-label="Increase quantity"
              disabled={quantity >= MAX_EXTRA_SLOTS || loadingCheckout || submitting}
              onClick={() => handleQuantityChange(quantity + 1)}
            >
              +
            </button>
          </div>
          <div className="form-text">
            One payment for all slots (max {MAX_EXTRA_SLOTS}). After purchase this account will
            have <strong>{afterPurchase}</strong> licensed pond
            {afterPurchase === 1 ? "" : "s"}.
          </div>
        </div>

        {loadingCheckout || !checkout ? (
          <div className="py-4 text-center text-muted">Updating payment amount…</div>
        ) : (
          <>
            {unitAmount != null && quantity > 1 ? (
              <p className="mb-1 text-muted">
                ${Number(unitAmount).toFixed(2)} × {quantity} slots
              </p>
            ) : null}
            <div className="display-6 fw-bold mb-3 text-dark">
              ${Number(totalAmount || 0).toFixed(2)}
              <small className="h6 text-body text-opacity-50 ms-1">/ year</small>
            </div>
            <div className="mb-3 p-3 border rounded">
              <label className="form-label fw-semibold">Card</label>
              <CardElement options={stripeCardElementOptions} />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !stripe || loadingCheckout}
            >
              {submitting
                ? "Processing…"
                : quantity === 1
                  ? "Pay & add 1 pond slot"
                  : `Pay & add ${quantity} pond slots`}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

const CompanyExtraPondPayment = () => (
  <Elements stripe={stripePromise}>
    <ExtraPondForm />
  </Elements>
);

export default CompanyExtraPondPayment;
